import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useI18n } from '../../contexts/I18nContext';
import {
  LOCALES,
  OG_LOCALES,
  SEO_ROUTES,
  SITE_NAME,
  SITE_NAMES,
  SITE_URL,
  classifySeoRoute,
  getLocalizedSeo,
  localizePath,
} from '../../seo/seoRoutes';
import { useEffect } from 'react';

/**
 * Injects canonical, hreflang, and Open Graph locale metadata.
 */
export default function SeoHreflang() {
  const { lang } = useI18n();
  const { pathname } = useLocation();
  const classification = classifySeoRoute(pathname);
  const fallbackEntry = classification.entry || SEO_ROUTES[0];
  const metadata = getLocalizedSeo(fallbackEntry, lang);
  const canonical = `${SITE_URL}${localizePath(classification.path, lang)}`;
  const siteName = SITE_NAMES[lang] || SITE_NAME;
  const title = metadata.title.includes(siteName) ? metadata.title : `${metadata.title} | ${siteName}`;

  const links = LOCALES.map(locale => ({
    rel: 'alternate',
    hrefLang: locale,
    href: `${SITE_URL}${localizePath(classification.path, locale)}`,
  }));
  links.push({
    rel: 'alternate',
    hrefLang: 'x-default',
    href: `${SITE_URL}${localizePath(classification.path, 'ko')}`,
  });

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': classification.path === '/' ? 'WebSite' : 'WebPage',
    name: metadata.title,
    description: metadata.description,
    url: canonical,
    inLanguage: lang,
    isPartOf: {
      '@type': 'WebSite',
      name: siteName,
      url: SITE_URL,
    },
  };

  useEffect(() => {
    if (classification.indexable) return undefined;

    const siteNames = { ko: '사주팔자AI', en: 'Saju AI', ja: '四柱八字AI', ru: 'Сажу AI', de: 'Saju AI' };
    const syncPrivateHead = () => {
      const heading = document.querySelector('main h1, main h2');
      const pageName = heading?.textContent?.trim();
      document.title = pageName ? `${pageName} | ${siteNames[lang]}` : siteNames[lang];

      let robots = document.querySelector('meta[name="robots"]');
      if (!robots) {
        robots = document.createElement('meta');
        robots.setAttribute('name', 'robots');
        document.head.appendChild(robots);
      }
      robots.setAttribute('content', 'noindex, nofollow');

      let canonicalLink = document.querySelector('link[rel="canonical"]');
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute('href', canonical);
      return Boolean(pageName);
    };

    if (syncPrivateHead()) return undefined;
    const observer = new MutationObserver(() => {
      if (syncPrivateHead()) observer.disconnect();
    });
    observer.observe(document.querySelector('main') || document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [canonical, classification.indexable, lang, pathname]);

  if (!classification.indexable) {
    return (
      <Helmet htmlAttributes={{ lang }}>
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href={canonical} />
      </Helmet>
    );
  }

  return (
    <Helmet htmlAttributes={{ lang }}>
      <title>{title}</title>
      <meta name="description" content={metadata.description} />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={canonical} />
      {links.map(l => (
        <link key={l.hrefLang} rel={l.rel} hrefLang={l.hrefLang} href={l.href} />
      ))}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={metadata.description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:locale" content={OG_LOCALES[lang]} />
      {LOCALES.filter(locale => locale !== lang).map(locale => (
        <meta key={locale} property="og:locale:alternate" content={OG_LOCALES[locale]} />
      ))}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={metadata.description} />
      <meta name="twitter:url" content={canonical} />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  );
}
